import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ApiService} from 'src/app/shared/services/api.service';
import {Payment} from 'src/app/shared/models/payment';
import {Place} from 'src/app/shared/models/place';
import {addDays, addMonths, endOfMonth, format, getDay, getDaysInMonth, startOfMonth, subMonths, getDayOfYear} from 'date-fns';
import {isPortOnlinePayable} from 'src/app/shared/models/default-filter-strategy';
import {RequestService} from 'src/app/shared/services/request.service';
import {CartService} from 'src/app/shared/services/cart.service';

export interface TimeSlot {
  time: string;
  date: Date;
  available: boolean;
  bookedCount: number;
  maxCapacity: number;
  remainingSpots: number;
}

export interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isOpenDay: boolean;
  isPastDay: boolean;
  isClosedDay: boolean; // Nouvelle propriété pour les jours fermés
  availabilityStatus?: 'high' | 'medium' | 'none';
  totalSlots?: number;
  availableSlots?: number;
}

@Component({
  selector: 'app-dialog-book-time-slot',
  templateUrl: './dialog-book-time-slot.component.html',
  styleUrl: './dialog-book-time-slot.component.scss'
})
export class DialogBookTimeSlotComponent implements OnDestroy {

  // ==================== CALENDAR PROPERTIES ====================
  currentDisplayedMonthDate: Date = new Date();
  bookingTimesSlots: number[] = [];
  timeSlotDuration: number = this.api.userInfo.server.paymentTimeSlotDuration;
  modeLocal: 'open' | 'closed' = 'open';
  maxDate: Date | null = null;

  // Calendar configuration
  calendarDays: CalendarDay[] = [];
  weekDays: string[] = [
    'calendar.weekday.sunday',
    'calendar.weekday.monday', 
    'calendar.weekday.tuesday',
    'calendar.weekday.wednesday',
    'calendar.weekday.thursday',
    'calendar.weekday.friday',
    'calendar.weekday.saturday'
  ];
  monthNames: string[] = [
    'calendar.month.january',
    'calendar.month.february',
    'calendar.month.march',
    'calendar.month.april',
    'calendar.month.may',
    'calendar.month.june',
    'calendar.month.july',
    'calendar.month.august',
    'calendar.month.september',
    'calendar.month.october',
    'calendar.month.november',
    'calendar.month.december'
  ];
  
  // ==================== PLACE INFORMATION ====================
  selectedPlace: Place | null = null;
  placeOpeningDays: number[] = []; // 0-6 (Sunday to Saturday)
  placeOpeningHours: {from: number, to: number; day?: number}[] = [];
  placeClosedDays: number[] = [];
  
  // ==================== TIME SLOT MANAGEMENT ====================
  selectedDate: Date | null = null;
  timeSlots: TimeSlot[] = [];
  showTimeline: boolean = false;
  timelineClosing: boolean = false;
  
  // Monthly availability cache to minimize API calls
  monthlyAvailability: Map<number, number[]> = new Map();
  
  // Loading states
  isLoadingMonth: boolean = false;
  
  // Auto-refresh interval for real-time availability updates
  private refreshInterval: any = null;
  private readonly REFRESH_INTERVAL_MS = 20000; // 20 seconds
  
  // ==================== BOOKING CONFIRMATION ====================
  confirmMode: boolean = false;
  selectedTimeSlot: TimeSlot | null = null;
  
  // Booking states
  isBooking: boolean = false;
  bookingError: string | null = null;
  bookingSuccess: boolean = false;
  
  // Auto-proceed timer for payment
  countdown: number = 5;
  private countdownInterval: any = null;
  isPortOnlinePayable = isPortOnlinePayable;

  constructor(private api: ApiService, 
    public dialogRef: MatDialogRef<DialogBookTimeSlotComponent>,
    public request: RequestService,
    public cart: CartService,

    @Inject(MAT_DIALOG_DATA) public data: {
      payment: Payment, 
      placeId: string, 
      paymentRequest: Document,
      selectedMonth?: Date,
      selectedDay?: Date,
      usageUntil: string
    }) {}

  ngOnInit() {
    this.isLoadingMonth = true;
    this.calculateMaxDate();
    this.getBookingPlace().then(async () => {
      this.generateCalendar();
      await this.updateMonthAvailability();
      await this.findNextAvailableMonth();
      if(this.data.selectedDay) {
        this.selectedDate = new Date(this.data.selectedDay);
        this.generateTimeSlots();
        this.showTimeline = true;
      }
      this.isLoadingMonth = false;
    });
  }

  private async findNextAvailableMonth(): Promise<void> {
    let safety = 12;
    while(safety-- > 0) {
      const hasAvailability = this.calendarDays.some(day => day.isCurrentMonth &&
      day.availabilityStatus !== 'none' && !day.isPastDay && day.isOpenDay && !day.isClosedDay);

      if(hasAvailability) return;
      this.currentDisplayedMonthDate = addMonths(this.currentDisplayedMonthDate, 1);
      this.generateCalendar();
      this.isLoadingMonth = true;
      await this.updateMonthAvailability(true);
    }
  }


  ngOnDestroy() {
    this.cleanupTimers();
  }

  /**
   * Cleanup all running timers to prevent memory leaks
   */
  private cleanupTimers() {
    this.cleanupAutoRefresh();
    this.stopCountdown();
  }

  /**
   * Load place information and configure opening hours/days
   */
  async getBookingPlace(): Promise<void> {
    try {
      const places = await this.api.publicPlaces();
      this.selectedPlace = places.find((p: any) => p.id === this.data.placeId);
      if(this.selectedPlace) {
        this.placeOpeningDays = (this.selectedPlace.openingDays || []).filter((d: any): d is number => d !== null && d !== undefined);
        this.placeOpeningHours = this.selectedPlace.openingHours || [];
        this.placeClosedDays = this.parseClosedDays(this.selectedPlace.closedDays || []);
        this.modeLocal = (this.placeOpeningDays.length === 0) ? 'closed' : 'open';
        this.generateCalendar(); // Regenerate with place info
      }
    } catch (error) {
      console.error('Error loading place data:', error);
    }
  }


  private parseClosedDays(closedDays: (string | number | null)[]): number[] {
    const year = this.currentDisplayedMonthDate.getFullYear();

    const toDayOfYear = (d: Date) => getDayOfYear(d); // 1..366

    return closedDays
      .filter(v => v !== null && v !== undefined)
      .map(v => {
        if(typeof v === 'number') {
          if(v >= 1 && v <= 366) return v;
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : toDayOfYear(d);
        } else {
          const d = new Date(v);
          if(isNaN(d.getTime())) return null;
          if(!/^\d{4}-/.test(v)) {
            d.setFullYear(year);
          }
          return toDayOfYear(d);
        }
      })
      .filter((n): n is number => n !== null);
  }


  private isClosedDay(date: Date): boolean {
    const dayOfYear = getDayOfYear(date);
    return this.placeClosedDays.includes(dayOfYear);
  }

  private calculateMaxDate(): void {
    const today = new Date();
    const oneYearFromNow = addDays(today, 365);
    
    if(this.data.usageUntil) {
      try {
        const usageUntilDate = new Date(this.data.usageUntil);
        if(!isNaN(usageUntilDate.getTime())) {
          this.maxDate = usageUntilDate < oneYearFromNow ? usageUntilDate : oneYearFromNow;
        } else {
          this.maxDate = oneYearFromNow;
        }
      } catch (error) {
        this.maxDate = oneYearFromNow;
      }
    } else {
      this.maxDate = oneYearFromNow;
    }
  }

  // ==================== CALENDAR GENERATION ====================

  /**
   * Generate calendar grid for the current month
   */
  generateCalendar() {
    this.calendarDays = [];
    const firstDayOfMonth = startOfMonth(this.currentDisplayedMonthDate);
    const lastDayOfMonth = endOfMonth(this.currentDisplayedMonthDate);
    const firstDayWeekday = getDay(firstDayOfMonth);
    const daysInMonth = getDaysInMonth(this.currentDisplayedMonthDate);
    const today = new Date();

    // Add days from previous month to fill the first week
    for(let i = firstDayWeekday - 1; i >= 0; i--) {
      const date = addDays(firstDayOfMonth, -i - 1);
      this.calendarDays.push(this.createCalendarDay(date, false, false, false, true, false));
    }
    
    // Add days of current month
    for(let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.currentDisplayedMonthDate.getFullYear(), this.currentDisplayedMonthDate.getMonth(), day);
      const isToday = this.isSameDay(date, today);
      const isPastDay = (date < today && !isToday) || (this.maxDate && date > this.maxDate);
      const isClosedDay = this.isClosedDay(date);

      let isOpenDay = false;
      if(this.modeLocal === 'open') {
        const dayOfWeek  = getDay(date);
        const hasSpecific = this.placeOpeningHours.some(h => h.day === dayOfWeek);
        const hasGlobal = this.placeOpeningHours.some(h => h.day === undefined);
        isOpenDay = (hasSpecific || hasGlobal) && !isClosedDay && (!this.maxDate || date <= this.maxDate);
      } else {
        isOpenDay = !isClosedDay && (!this.maxDate || date <= this.maxDate);
      }

      this.calendarDays.push(this.createCalendarDay(date, true, isToday, isOpenDay, isPastDay, isClosedDay));
    }
    
    // Add days from next month to fill the last week (6 weeks * 7 days = 42 cells)
    const remainingCells = 42 - this.calendarDays.length;
    for(let day = 1; day <= remainingCells; day++) {
      const date = addDays(lastDayOfMonth, day);
      this.calendarDays.push(this.createCalendarDay(date, false, false, false, true, false));
    }
  }

  private createCalendarDay(date: Date, isCurrentMonth: boolean, isToday: boolean, isOpenDay: boolean, isPastDay: boolean, isClosedDay: boolean): CalendarDay {
    return {
      date,
      day: date.getDate(),
      isCurrentMonth,
      isToday,
      isOpenDay,
      isPastDay,
      isClosedDay
    };
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
  }

  previousMonth() {
    this.navigateMonth(-1);
  }

  nextMonth() {
    this.navigateMonth(1);
  }

  /**
   * Navigate to previous/next month and refresh availability
   */
  private navigateMonth(direction: number) {
    this.currentDisplayedMonthDate = direction > 0 ? 
      addMonths(this.currentDisplayedMonthDate, 1) : 
      subMonths(this.currentDisplayedMonthDate, 1);
    
    this.generateCalendar();
    this.hideTimeline();
    this.clearAvailabilityCache();
    this.isLoadingMonth = true;
    this.updateMonthAvailability();
  }

  private clearAvailabilityCache() {
    this.monthlyAvailability.clear();
    this.cleanupAutoRefresh();
  }

  getCurrentMonthKey(): string {
    return this.monthNames[this.currentDisplayedMonthDate.getMonth()];
  }

  getCurrentYear(): number {
    return this.currentDisplayedMonthDate.getFullYear();
  }

  // ==================== AVAILABILITY MANAGEMENT ====================

  /**
   * Update monthly availability data from API
   */
  async updateMonthAvailability(isAutoRefresh: boolean = false) {
    if(!this.selectedPlace) {
      this.isLoadingMonth = false;
      return;
    }
    
    const firstDayOfMonth = startOfMonth(this.currentDisplayedMonthDate);
    const lastDayOfMonth = endOfMonth(this.currentDisplayedMonthDate);
    
    // API call uses middle of month (15th) for optimal data retrieval
    const middleOfMonth = new Date(this.currentDisplayedMonthDate.getFullYear(),
      this.currentDisplayedMonthDate.getMonth(),
      15, 12, 0, 0, 0);
    
    try {
      let response: any;
      if(this.data.payment.id) {
        response = await this.api.findTimeslots(this.data.payment.id, middleOfMonth);
      } else if(this.data.payment.ids) {
        //TODO need to adapt this to handle multiple ids if still necessary
        response = await this.api.findTimeslots(this.data.payment.ids[0], middleOfMonth);
      } else {
        return;
      }
      const bookingsArray = response.result || response;
      
      if(!Array.isArray(bookingsArray)) {
        console.error('Invalid API response format:', typeof bookingsArray);
        return;
      }
      
      // Convert ISO strings to timezone-neutral timestamps for consistent comparison
      const timestamps: number[] = this.convertBookingsToTimestamps(bookingsArray);
      
      this.monthlyAvailability.clear();
      this.processMonthlyAvailability(timestamps, firstDayOfMonth, lastDayOfMonth);
      
      this.isLoadingMonth = false;
      
      // Update calendar days availability status
      this.updateCalendarDaysAvailability();
      
      if(!isAutoRefresh) {
        this.startAutoRefresh();
      }
      
      // Refresh timeline if visible
      if(this.selectedDate && this.showTimeline) {
        this.getBookingTimesSlots();
      }
    } catch (error) {
      console.error('Error loading monthly availability:', error);
      this.isLoadingMonth = false;
    }
  }

  /**
   * Convert API booking data to timezone-neutral timestamps
   */
  private convertBookingsToTimestamps(bookingsArray: (string | number)[]): number[] {
    return bookingsArray.map((item: string | number) => {
      if(typeof item === 'string') {
        // Parse ISO string and treat as timezone-neutral time
        const isoDate = new Date(item);
        return new Date(Date.UTC(isoDate.getUTCFullYear(),
          isoDate.getUTCMonth(),
          isoDate.getUTCDate(),
          isoDate.getUTCHours(),
          isoDate.getUTCMinutes(),
          isoDate.getUTCSeconds(),
          isoDate.getUTCMilliseconds())).getTime();
      }
      return item; // Already a timestamp
    });
  }

  /**
   * Process and cache availability data by day
   */
  private processMonthlyAvailability(bookedTimestamps: number[], firstDay: Date, lastDay: Date) {
    const dailyBookings = new Map<string, number[]>();
    
    // Group bookings by date
    bookedTimestamps.forEach(timestamp => {
      const date = new Date(timestamp);
      if(date >= firstDay && date <= lastDay) {
        const dateKey = format(date, 'yyyy-MM-dd');
        if(!dailyBookings.has(dateKey)) {
          dailyBookings.set(dateKey, []);
        }
        dailyBookings.get(dateKey)!.push(timestamp);
      }
    });
    
    // Store in cache with consistent midnight UTC keys
    dailyBookings.forEach((timestamps, dateKey) => {
      const date = new Date(dateKey + 'T00:00:00.000Z');
      const cacheKey = date.getTime();
      this.monthlyAvailability.set(cacheKey, timestamps);
    });
  }

  // ==================== DAY SELECTION & TIME SLOTS ====================

  /**
   * Handle calendar day selection
   */
  selectDay(calendarDay: CalendarDay) {
    // Handle navigation to different month if clicking on other-month day
    if(!calendarDay.isCurrentMonth) {
      const targetMonth = new Date(calendarDay.date.getFullYear(), calendarDay.date.getMonth(), 1);
      const targetDate = calendarDay.date;
      this.currentDisplayedMonthDate = targetMonth;
      this.generateCalendar();
      this.hideTimeline();
      this.clearAvailabilityCache();
      this.isLoadingMonth = true;
      
      // Update month availability and then select the day
      this.updateMonthAvailability().then(() => {
        // Small delay to ensure calendar day availability is calculated
        setTimeout(() => {
          const updatedCalendarDay = this.calendarDays.find(day => day.date.getTime() === targetDate.getTime());
          if(updatedCalendarDay && this.isValidDaySelection(updatedCalendarDay)) {
            this.selectedDate = updatedCalendarDay.date;
            this.generateTimeSlots();
            this.showTimeline = true;
          }
        }, 100);
      }).catch(() => {
        // If async fails, try selecting the day anyway
        setTimeout(() => {
          const updatedCalendarDay = this.calendarDays.find(day => day.date.getTime() === targetDate.getTime());
          if(updatedCalendarDay && this.isValidDaySelection(updatedCalendarDay)) {
            this.selectedDate = updatedCalendarDay.date;
            this.generateTimeSlots();
            this.showTimeline = true;
          }
        }, 100);
      });
      return;
    }
    
    if(!this.isValidDaySelection(calendarDay)) {
      return;
    }
    
    // Toggle timeline if same day selected
    if(this.selectedDate && calendarDay.date.getTime() === this.selectedDate.getTime()) {
      this.hideTimeline();
      return;
    }
    
    this.selectedDate = calendarDay.date;
    this.generateTimeSlots();
    this.showTimeline = true;
  }

  private isValidDaySelection(calendarDay: CalendarDay): boolean {
    /* return calendarDay.isCurrentMonth && 
           !calendarDay.isPastDay && 
           calendarDay.isOpenDay &&
           !calendarDay.isClosedDay &&
           calendarDay.availabilityStatus !== 'none' &&*/
    if(!calendarDay.isCurrentMonth || calendarDay.isPastDay || calendarDay.isClosedDay || calendarDay.availabilityStatus === 'none') {
      return false;
    }

    const dayOfWeek = getDay(calendarDay.date);
    if(this.modeLocal === 'open') {
      return this.placeOpeningDays.includes(dayOfWeek);
    } else {
      return !this.isClosedDay(calendarDay.date);
    }
  }

  /**
   * Generate available time slots for selected date
   */
  generateTimeSlots() {
    if(!this.selectedDate || !this.selectedPlace) return;
    this.timeSlots = [];
    const selectedDayOfWeek = getDay(this.selectedDate);
    const today = new Date();
    const isToday = this.isSameDay(this.selectedDate, today);
    const isClosed = this.isClosedDay(this.selectedDate);
    // Check if the day is open and not closed
    if((this.modeLocal === 'open' && !this.placeOpeningDays.includes(selectedDayOfWeek)) || (this.modeLocal === 'closed' && isClosed)) {
      return;
    }
    const hasSpecificHours = this.placeOpeningHours.some(h => h.day === selectedDayOfWeek);
    const validRanges = hasSpecificHours ? this.placeOpeningHours.filter(h => h.day === selectedDayOfWeek) : this.placeOpeningHours.filter(h => h.day === undefined);
  
    // Generate slots for each opening hour period
    validRanges.forEach(hours => {
      this.generateSlotsForPeriod(hours, isToday);
    });

    this.timeSlots.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Apply availability from cache
    this.getBookingTimesSlots();
  }

  /**
   * Generate time slots for a specific opening hour period
   */
  private generateSlotsForPeriod(hours: { from: number; to: number }, isToday: boolean) {
    const placeOffset = this.selectedPlace.timezoneOffsetGmtMinutes * 60000 || 0;
    let currentUtc = new Date(Date.UTC(this.selectedDate!.getFullYear(), this.selectedDate!.getMonth(), this.selectedDate!.getDate(), hours.from, 0, 0, 0));
    const endUtc = new Date(Date.UTC(this.selectedDate!.getFullYear(), this.selectedDate!.getMonth(), this.selectedDate!.getDate(), hours.to, 0, 0, 0));

    if(isToday) {
      const cutoffUtc = new Date(Date.now() + placeOffset + 2 * 60 * 60 * 1000);
      if(currentUtc <= cutoffUtc) {
        const slotsToSkip = Math.ceil((cutoffUtc.getTime() - currentUtc.getTime()) / this.timeSlotDuration);
        currentUtc = new Date(currentUtc.getTime() + slotsToSkip * this.timeSlotDuration);
      }
    }

    while(currentUtc < endUtc) {
      const slotEndUtc = new Date(currentUtc.getTime() + this.timeSlotDuration);
      if(slotEndUtc > endUtc) break;
      if(this.maxDate && slotEndUtc > this.maxDate) break;

      const currentLocal = new Date(currentUtc.getTime() + placeOffset);

      const maxCapacity = this.getMaxConcurrentBookings();
      this.timeSlots.push({
        time: format(currentLocal, 'HH:mm'),
        date: currentUtc,
        available: true,
        bookedCount: 0,
        maxCapacity,
        remainingSpots: maxCapacity
      });

      currentUtc = new Date(currentUtc.getTime() + this.timeSlotDuration);
    }
  }

  hideTimeline() {
    this.timelineClosing = true;
    setTimeout(() => {
      this.showTimeline = false;
      this.selectedDate = null;
      this.timeSlots = [];
      this.timelineClosing = false;
      this.resetBookingState();
    }, 250);
  }

  /**
   * Apply booking availability from cache to time slots
   */
  getBookingTimesSlots() {
    if(!this.selectedDate) return;
    
    // Get cached bookings for selected date
    const selectedDateString = format(this.selectedDate, 'yyyy-MM-dd');
    const dateKey = new Date(selectedDateString + 'T00:00:00.000Z').getTime();
    
    this.bookingTimesSlots = this.monthlyAvailability.get(dateKey) || [];
    
    // Count bookings per time slot and update availability
    this.timeSlots.forEach(slot => {
      const slotTimestamp = slot.date.getTime();
      
      // Count how many bookings exist for this exact time slot
      slot.bookedCount = this.bookingTimesSlots.filter(timestamp => timestamp === slotTimestamp).length;
      
      // Calculate remaining spots
      slot.remainingSpots = Math.max(0, slot.maxCapacity - slot.bookedCount);
      
      // Slot is available if there are remaining spots
      slot.available = slot.remainingSpots > 0;
    });
  }

  // ==================== BOOKING PROCESS ====================

  /**
   * Initiate time slot selection for booking
   */
  selectTimeSlot(timeSlot: TimeSlot) {
    if(!timeSlot.available) return;
    
    this.selectedTimeSlot = timeSlot;
    this.confirmMode = true;
  }

  /**
   * Confirm and execute the booking
   */
  async confirmBooking() {
    if(!this.selectedTimeSlot || this.isBooking) return;
    
    this.isBooking = true;
    this.bookingError = null;
    
    try {
      if(this.data.payment.id) {
        await this.api.bookTimeslot(this.data.payment.id, this.selectedTimeSlot.date);
      } else if(this.data.payment.ids) {
        //TODO need to adapt this to handle multiple ids if still necessary
        await this.api.bookTimeslot(this.data.payment.ids[0], this.selectedTimeSlot.date);
      } else {
        return;
      }
      this.isBooking = false;
      this.bookingSuccess = true;
      this.startCountdown();
    } catch (error: any) {
      this.isBooking = false;
      this.bookingError = error.message || 'An error occurred while booking the time slot';
    }
  }

  /**
   * Cancel booking and return to timeline
   */
  cancelBooking() {
    this.confirmMode = false;
    this.selectedTimeSlot = null;
    this.bookingError = null;
    this.bookingSuccess = false;
    this.isBooking = false;
    this.stopCountdown();
  }

  /**
   * Proceed to payment after successful booking
   */
  proceedToPayment() {
    this.stopCountdown();
    this.closeDialog(this.data.payment);
  }

  retryBooking() {
    this.bookingError = null;
    this.bookingSuccess = false;
  }

  private resetBookingState() {
    this.confirmMode = false;
    this.selectedTimeSlot = null;
    this.bookingError = null;
    this.bookingSuccess = false;
    this.isBooking = false;
    this.stopCountdown();
  }

  // ==================== COUNTDOWN TIMER ====================

  /**
   * Start 5-second countdown for auto-proceed to payment
   */
  private startCountdown() {
    this.countdown = 5;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if(this.countdown <= 0) {
        this.stopCountdown();
        this.proceedToPayment();
      }
    }, 1000);
  }

  private stopCountdown() {
    if(this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
      this.countdown = 5;
    }
  }

  // ==================== TEMPLATE HELPERS ====================

  getSelectedDateFormatted(): string {
    if(!this.selectedDate) return '';
    return format(this.selectedDate, 'EEEE d MMMM yyyy');
  }

  getSelectedDayKey(): string {
    if(!this.selectedDate) return '';
    const dayIndex = getDay(this.selectedDate);
    return this.weekDays[dayIndex];
  }

  getSelectedMonthKey(): string {
    if(!this.selectedDate) return '';
    return this.monthNames[this.selectedDate.getMonth()];
  }

  getSelectedDayNumber(): number {
    if(!this.selectedDate) return 0;
    return this.selectedDate.getDate();
  }

  getSelectedYear(): number {
    if(!this.selectedDate) return 0;
    return this.selectedDate.getFullYear();
  }

  /**
   * Get max concurrent bookings for the current place, defaults to 1 if not set
   */
  private getMaxConcurrentBookings(): number {
    return this.selectedPlace?.maxConcurrentBookings || 1;
  }

  /**
   * Check if this calendar day is the last day of the week row for the selected date
   */
  isEndOfSelectedWeek(index: number): boolean {
    if(!this.selectedDate) return false;
    
    // Find the index of the selected date in calendarDays
    const selectedIndex = this.calendarDays.findIndex(day => day.date.getTime() === this.selectedDate!.getTime());
    
    if(selectedIndex === -1) return false;
    
    // Calculate which week row the selected date is in (0-based)
    const selectedWeekRow = Math.floor(selectedIndex / 7);
    
    // Calculate which week row the current day is in
    const currentWeekRow = Math.floor(index / 7);
    
    // Check if we're in the same week as selected date
    if(selectedWeekRow !== currentWeekRow) return false;
    
    // Check if this is the last day of the week (Saturday - index % 7 === 6)
    // OR if this is the last day in the calendar days array for this week
    const isLastDayOfWeek = (index + 1) % 7 === 0;
    const isLastDayInArray = index === this.calendarDays.length - 1;
    const isLastDayOfSelectedWeek = isLastDayOfWeek || (isLastDayInArray && currentWeekRow === selectedWeekRow);
    
    return isLastDayOfSelectedWeek;
  }

  /**
   * Calculate availability status for a specific day
   */
  private calculateDayAvailability(calendarDay: CalendarDay): void {
    if(!calendarDay.isCurrentMonth || calendarDay.isPastDay || !calendarDay.isOpenDay || calendarDay.isClosedDay || !this.selectedPlace) {
      calendarDay.availabilityStatus = 'none';
      calendarDay.totalSlots = 0;
      calendarDay.availableSlots = 0;
      return;
    }

    // Generate time slots for this day to calculate availability
    const daySlots = this.generateTimeSlotsForDay(calendarDay.date);
    const maxCapacity = this.getMaxConcurrentBookings();
    
    // Get bookings for this day
    const dateKey = new Date(format(calendarDay.date, 'yyyy-MM-dd') + 'T00:00:00.000Z').getTime();
    const dayBookings = this.monthlyAvailability.get(dateKey) || [];
    
    let totalAvailableSlots = 0;
    const totalSlots = daySlots.length;
    
    // Calculate available slots considering concurrent bookings
    daySlots.forEach(slot => {
      const slotTimestamp = slot.date.getTime();
      const bookedCount = dayBookings.filter(timestamp => timestamp === slotTimestamp).length;
      const remainingSpots = Math.max(0, maxCapacity - bookedCount);
      totalAvailableSlots += remainingSpots;
    });
    
    calendarDay.totalSlots = totalSlots * maxCapacity;
    calendarDay.availableSlots = totalAvailableSlots;

    // Determine availability status based on percentage
    if(totalAvailableSlots === 0 || totalSlots === 0) {
      calendarDay.availabilityStatus = 'none';  // 0% - completely booked
    } else {
      const availabilityPercentage = totalAvailableSlots / (totalSlots * maxCapacity);
      if(availabilityPercentage > 0.5) {
        calendarDay.availabilityStatus = 'high';    // 51-100% disponible - vert
      } else {
        calendarDay.availabilityStatus = 'medium';  // 1-50% disponible - orange
      }
    }
  }

  /**
   * Generate time slots for a specific day (similar to generateTimeSlots but for any day)
   */
  private generateTimeSlotsForDay(date: Date): TimeSlot[] {
    if(!this.selectedPlace) return [];
    const slots: TimeSlot[] = [];
    const dayOfWeek = getDay(date);
    const today = new Date();
    const isToday = this.isSameDay(date, today);

    const isClosed = this.isClosedDay(date);
    if((this.modeLocal === 'open' && !this.placeOpeningDays.includes(dayOfWeek)) || (this.modeLocal === 'closed' && isClosed)) {
      return slots;
    }

    const placeOffset = this.selectedPlace.timezoneOffsetGmtMinutes  * 60000 || 0;

    const hasSpecificHours = this.placeOpeningHours.some(h => h.day === dayOfWeek);
    const ranges = this.placeOpeningHours.filter(h => hasSpecificHours ? h.day === dayOfWeek : h.day === undefined);

    ranges.forEach(hours => {
      let currentUtc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hours.from, 0, 0, 0));
      const endUtc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hours.to, 0, 0, 0));

      if(isToday) {
        const cutoffUtc = new Date(Date.now() + placeOffset + 2 * 60 * 60 * 1000);
        if(currentUtc <= cutoffUtc) {
          const slotsToSkip = Math.ceil((cutoffUtc.getTime() - currentUtc.getTime()) / this.timeSlotDuration);
          currentUtc = new Date(currentUtc.getTime() + slotsToSkip * this.timeSlotDuration);
        }
      }

      while(currentUtc < endUtc) {
        const slotEndUtc = new Date(currentUtc.getTime() + this.timeSlotDuration);
        if(slotEndUtc > endUtc) break;
        if(this.maxDate && slotEndUtc > this.maxDate) break;

        const currentLocal = new Date(currentUtc.getTime() + placeOffset);
        const maxCapacity = this.getMaxConcurrentBookings();
        slots.push({
          time: format(currentLocal, 'HH:mm'), 
          date: currentUtc,                  
          available: true,
          bookedCount: 0,
          maxCapacity,
          remainingSpots: maxCapacity
        });

        currentUtc = new Date(currentUtc.getTime() + this.timeSlotDuration);
      }
    });

    return slots;
  }


  /**
   * Update availability status for all calendar days
   */
  private updateCalendarDaysAvailability(): void {
    this.calendarDays.forEach(day => {
      this.calculateDayAvailability(day);
    });
  }

  closeDialog(done?: Payment): void {
    this.cleanupAutoRefresh();
    this.dialogRef.close(done);
  }

  // ==================== AUTO REFRESH ====================

  /**
   * Start automatic refresh of availability data
   */
  private startAutoRefresh() {
    this.cleanupAutoRefresh();
    this.refreshInterval = setInterval(() => {
      this.updateMonthAvailability(true);
    }, this.REFRESH_INTERVAL_MS);
  }

  private cleanupAutoRefresh() {
    if(this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  getBookingDots(timeSlot: TimeSlot): number[] {
    return Array.from({length: timeSlot.maxCapacity}, (_, i) => i);
  }

  getSlotEndTime(timeSlot: TimeSlot): Date {
    return new Date(timeSlot.date.getTime() + this.timeSlotDuration);
  }
}
